import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { MailAppStateService } from './mail-app-state.service';
import { MailReaderOpenService } from './mail-reader-open.service';

/** What a mounted rail folder tree offers: to open one of its folders exactly as a click on its row does. */
export interface MailRailFolderTree {
  /** `false` when this tree does not hold that folder. */
  openFolder(folderId: string): boolean;
}

/** The rail's sections, which mount a mailbox's folder tree only while they are expanded. */
export interface MailRailSections {
  /** Expands the section that shows `tenantId`'s mailbox; `false` when no section shows it. */
  expandFor(tenantId: string): boolean;
}

/**
 * The Mail rail's folder trees, while they are mounted (a team mounts My Workspace's and Team Folders at once).
 *
 * The search popup's "Open folder" goes through here so it does what a click on that folder in the rail does — the
 * row selected, its mailbox chosen, the list showing the folder's emails, the folder in the reader — by asking the
 * tree that holds the folder to run its own open, rather than repeating any of it (searchopen r4, Pavel 2026-10-29:
 * "it should open in 3rd panel as if I would click it in nav rail, which updates 2nd panel, and 3rd panel").
 *
 * A collapsed section has no tree (Codex on #2758, reproduced live): the section is expanded, as a person would
 * before the click, and the open waits until that section's tree holds its folders.
 */
@Injectable()
export class MailRailFolderTrees {
  private readonly trees = new Set<MailRailFolderTree>();
  private sections: MailRailSections | null = null;
  /** The folder an open is waiting for, until the tree that shows it has its folders. */
  private pendingFolderId: string | null = null;

  constructor() {
    // Anything opened meanwhile is the newer choice, and the waiting open is dropped, or its late arrival would replace
    // it (Codex on #2758, each reproduced live): a folder clicked in the rail (it fills the reader at once), an email
    // opened from the list or the popup — dropped as it is ASKED for, since its read may answer after the section's
    // folders do — and a rail row such as Inbox or Flagged. The waiting open's own run clears it first, so it does not
    // cancel itself.
    const state = inject(MailAppStateService);
    merge(state.mainViewRequests$, state.virtualFolderRequests$, inject(MailReaderOpenService).requests$)
      .pipe(takeUntilDestroyed())
      .subscribe(() => (this.pendingFolderId = null));
  }

  public register(tree: MailRailFolderTree): void {
    this.trees.add(tree);
  }

  public unregister(tree: MailRailFolderTree): void {
    this.trees.delete(tree);
  }

  public registerSections(sections: MailRailSections): void {
    this.sections = sections;
  }

  public unregisterSections(sections: MailRailSections): void {
    if (this.sections === sections) {
      this.sections = null;
    }
  }

  /**
   * Opens the folder of `tenantId`'s mailbox through the tree that holds it, expanding its section first when that is
   * collapsed. `false` when the rail shows no such folder at all.
   */
  public open(folderId: string, tenantId: string): boolean {
    this.pendingFolderId = null;
    for (const tree of this.trees) {
      if (tree.openFolder(folderId)) {
        return true;
      }
    }
    if (this.sections?.expandFor(tenantId)) {
      this.pendingFolderId = folderId;
      return true;
    }
    return false;
  }

  /**
   * Drops the waiting open: its section was collapsed again before its folders arrived, so expanding it later must
   * not open the old folder over whatever the person is looking at then (Codex r6 on #2758, reproduced live).
   */
  public cancelPending(): void {
    this.pendingFolderId = null;
  }

  /** Called by a tree whenever it has its folders: runs the waiting open if the folder is this tree's. */
  public openPendingIn(tree: MailRailFolderTree): void {
    const folderId = this.pendingFolderId;
    if (!folderId) {
      return;
    }
    // Cleared BEFORE the open: the open makes the tree receive its folders again, synchronously, which lands back here.
    this.pendingFolderId = null;
    if (!tree.openFolder(folderId)) {
      this.pendingFolderId = folderId;
    }
  }
}
